/**
 * Small helpers for reading the logged-in user/role from localStorage.
 * The actual security boundary is server-side (see require_roles in
 * the middleware) - this is just for the UI to show/hide the right
 * nav links and pages without a full round trip.
 */

/**
 * Logs out by removing only the auth-related keys - deliberately NOT
 * a blanket localStorage.clear(), since that would also wipe
 * unrelated device preferences that should survive a logout (Theme
 * Settings' "themeColor" key being the concrete case this was added
 * for - a blanket clear() meant picking a theme, then logging out,
 * silently reset it back to default). CookiePreferences.js's "clear
 * my local data" button is the one place a full clear() is still
 * correct - that's explicitly "wipe everything," not a logout.
 */
export function clearAuthStorage() {

    localStorage.removeItem("token");

    localStorage.removeItem("user");

    localStorage.removeItem("permissions");

}

export function getStoredUser() {

    try {

        const raw = localStorage.getItem("user");

        return raw ? JSON.parse(raw) : null;

    } catch {

        return null;

    }

}

export function getStoredRole() {

    return getStoredUser()?.role || null;

}

export function getStoredPermissions() {

    try {

        const raw = localStorage.getItem("permissions");

        return raw ? JSON.parse(raw) : {};

    } catch {

        return {};

    }

}

export function isLoggedIn() {

    return !!localStorage.getItem("token");

}

export function canView(menuKey) {

    const role = getStoredRole();

    if (role === ROLE_SUPERADMIN) {
        return true;
    }

    const perms = getStoredPermissions();

    return !!perms?.[menuKey]?.view;

}

// Returns {view, add, edit, delete} for one menu - what every page's
// Add/Edit/Delete buttons check before rendering. SuperAdmin always
// gets all four true, matching the backend's "SuperAdmin bypasses the
// matrix entirely" rule (see require_menu_permission in the
// middleware) - this mirrors that server-side truth so a button never
// shows here that would 403 there, and vice versa never hides one
// that would actually succeed.
export function getMenuPermissions(menuKey) {

    const role = getStoredRole();

    if (role === ROLE_SUPERADMIN) {

        return { view: true, add: true, edit: true, delete: true };

    }

    const perms = getStoredPermissions();

    return {

        view: !!perms?.[menuKey]?.view,

        add: !!perms?.[menuKey]?.add,

        edit: !!perms?.[menuKey]?.edit,

        delete: !!perms?.[menuKey]?.delete

    };

}

export const ROLE_SUPERADMIN = "superadmin";
export const ROLE_ADMIN = "admin";
export const ROLE_USER = "user";
